/**
 * orgChartBuilder — Office.js layer that draws an org chart on the active
 * PowerPoint slide. Ported from the Deckorate prototype's `orgChartBuilder.js`
 * (the default Office.js insertion path). Host access is guarded with
 * `hasPpt()` / `ensurePpt()`; usage/diagnostics flow through the shared
 * `src/telemetry` pipeline, tagged for the Deckorate connector — matching the
 * Format Tools and Map Plot features.
 *
 * Requires PowerPointApi 1.4 (addGeometricShape / addLine).
 */

import { logError, logEvent, scrubMessage } from "../../../../../telemetry";
import { computeConnectors, computeLayout, getInitials, luminance } from "./orgChartLayout";
import type {
  Connector,
  OrgCardTemplate,
  OrgChartOptions,
  OrgChartResult,
  OrgPerson,
  PositionedNode,
} from "../../../types";

/** Tag every telemetry event so it slices under the Deckorate connector. */
const ORG_CONNECTOR = "deckorate";

/** Shape line styling (points). */
const LINE_COLOR = "#ABABAB";
const LINE_WEIGHT = 1.5;

/** Shape-name prefixes so a chart can be identified/regrouped later. */
const CONNECTOR_PREFIX = "OrgLine_";
const CARD_PREFIX = "OrgCard_";
const NAME_PREFIX = "OrgName_";
const TITLE_PREFIX = "OrgTitle_";
const AVATAR_PREFIX = "OrgAvatar_";
const INITIALS_PREFIX = "OrgInitials_";
const PHOTO_PREFIX = "OrgPhoto_";

const DEFAULT_CARD_COLOR = "0078D4";
const WHITE = "FFFFFF";
const NAME_FONT_SIZE = 10;
const TITLE_FONT_SIZE = 9;

/* ── Card geometry tuning (all values in points unless noted) ── */
const CARD_BORDER_WEIGHT = 1;
/** Minimum cross-dimension for a connector so it never collapses to zero size. */
const MIN_CONNECTOR_DIM = 0.5;
/** Inner horizontal / top padding for card text. */
const TEXT_PAD_X = 4;
const TEXT_PAD_TOP = 2;
/** Avatar vertical inset within the card, and its width as a fraction of card width. */
const AVATAR_INSET = 8;
const AVATAR_WIDTH_RATIO = 0.35;
/** Initials font size as a fraction of the avatar size, with a floor. */
const AVATAR_FONT_RATIO = 0.38;
const MIN_AVATAR_FONT = 8;
/** Fraction of card height used by the name band when a title is also shown. */
const NAME_BAND_RATIO = 0.45;
/** Vertical inset for a name-only card (no title). */
const NAME_ONLY_INSET = 6;
/** Minimum title band height, and the gap reserved below the name. */
const MIN_TITLE_HEIGHT = 12;
const TITLE_GAP = 4;
/** Luminance above which the avatar fill is "light" (use dark initials). */
const LIGHT_LUMINANCE_THRESHOLD = 0.5;

/* ── User-facing error messages (named so wording stays consistent) ── */
const ERR_NO_PPT = "Org charts require running inside PowerPoint.";
const ERR_NO_PEOPLE = "Add at least one person before generating.";
const ERR_NO_SLIDES = "The presentation has no slides to draw on.";
const ERR_NO_SHAPE = "Select a shape on the slide to use as the card template.";

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

function ensurePpt(): void {
  if (!hasPpt()) 
  {
    throw new Error(ERR_NO_PPT);
  }
}

/**
 * Build the standardized error block from a caught value: the error's name and
 * a PII-scrubbed message, matching the shape the shared telemetry pipeline
 * expects (see `useOrgChart` / `useFormatTools`).
 */
function toTelemetryError(error: unknown): { type: string; scrubbedMessage: string } {
  return {
    type: error instanceof Error ? error.name : "DeckorateOrgChartError",
    scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
  };
}

/**
 * Record a non-fatal, best-effort failure through the shared error pipeline
 * (`error.connector.failure`, tagged Deckorate) — the same standardized channel
 * the other connector features use. The styling/grouping steps degrade the
 * chart's polish but not its correctness, so we continue after logging rather
 * than swallowing the error silently.
 */
function logOrgFailure(reason: string, error: unknown): void {
  logError("error.connector.failure", toTelemetryError(error), {
    connectorSystemName: ORG_CONNECTOR,
    reason,
  });
}

/**
 * Record an unexpected Office.js exception under the dedicated `error.office-js`
 * event before it either propagates to the caller or is turned into a safe
 * fallback, so a raw Office.js failure is never lost.
 */
function logOfficeJsError(reason: string, error: unknown): void {
  logError("error.office-js", toTelemetryError(error), {
    connectorSystemName: ORG_CONNECTOR,
    reason,
  });
}

/** PowerPointApi 1.4 check (addGeometricShape / addLine). */
export function isOrgChartSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", "1.4");
  } catch (e) {
    // Probe must always yield a boolean, so we log the exception (rather than
    // swallow it) and fall back to "unsupported" — the panel surfaces that
    // state to the user in place of a thrown notification.
    logOfficeJsError("org-chart-support-check", e);
    return false;
  }
}

/** A text box queued for font styling after it has been added and synced. */
interface StyledText {
  box: PowerPoint.Shape;
  bold: boolean;
  size: number;
  color: string; // hex without "#"
  align?: "center";
  vAlign?: boolean;
}

/** Resolved card styling for one generate run (hex WITHOUT "#"). */
interface CardStyle {
  fill: string;
  line: string;
  text: string;
}

/** Normalise a colour to 6-digit hex WITHOUT a leading "#", or null if unusable. */
function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const clean = value.replace(/^#/, "").trim();
  return /^[0-9a-fA-F]{6}$/.test(clean) ? clean.toUpperCase() : null;
}

/**
 * Resolve card styling from the chosen card colour and an optional captured
 * template. Template fields win when present; otherwise fill/line use the card
 * colour and text stays white.
 */
function resolveCardStyle(cardColor: string, template?: OrgCardTemplate | null): CardStyle {
  return {
    fill: normalizeHex(template?.fillColor) ?? cardColor,
    line: normalizeHex(template?.lineColor) ?? cardColor,
    text: normalizeHex(template?.fontColor) ?? WHITE,
  };
}

/** Avatar square geometry for a card (matches `addCard`'s placement maths). */
function avatarRect(node: PositionedNode): { left: number; top: number; size: number } {
  const size = Math.min(node.height - AVATAR_INSET, node.width * AVATAR_WIDTH_RATIO);
  const margin = (node.height - size) / 2;
  return { left: node.x + margin, top: node.y + (node.height - size) / 2, size };
}

/**
 * Generate an org chart. Draws on the active slide, or on a fresh slide when
 * `options.insertMode === "newSlide"`. Photos embed in either mode: the target
 * slide is selected first, then each photo is inserted onto it.
 *
 * @throws if `people` is empty or PowerPoint is unavailable.
 */
export async function generateOrgChart(
  people: readonly OrgPerson[],
  options: OrgChartOptions,
): Promise<OrgChartResult> {
  ensurePpt();
  if (people.length === 0) {
    throw new Error(ERR_NO_PEOPLE);
  }

  const layout = options.layout;
  const cardColor = (options.cardColor || DEFAULT_CARD_COLOR).replace(/^#/, "");
  const showPhotoHead = options.showPhotoHead ?? false;
  const insertMode = options.insertMode ?? "current";
  const style = resolveCardStyle(cardColor, options.template);

  const positioned = computeLayout(people, layout);
  const connectors = computeConnectors(positioned, layout);

  const slideId = await insertShapes(positioned, connectors, style, showPhotoHead, insertMode);

  // Overlay photos onto the same slide the cards were drawn on (works for the
  // current slide and a new slide alike — image insertion follows the selection).
  if (showPhotoHead) {
    await embedPhotos(positioned, slideId);
  }

  logEvent("connector.action.invoke", "usage", {
    connectorSystemName: ORG_CONNECTOR,
    reason: "org-chart-generate",
  });

  return { cardCount: positioned.length, connectorCount: connectors.length };
}

/**
 * Capture the fill, line and font colours of the first selected shape, to reuse
 * as the org-chart card template.
 *
 * @throws if no shape is selected or PowerPoint is unavailable.
 */
export async function captureCardTemplate(): Promise<OrgCardTemplate> {
  ensurePpt();
  return await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedShapes();
    selected.load("items");
    await ctx.sync();

    const shape = selected.items[0];
    if (!shape) {
      throw new Error(ERR_NO_SHAPE);
    }

    shape.fill.load("foregroundColor");
    shape.lineFormat.load("color,visible");
    await ctx.sync();

    const template: OrgCardTemplate = {};
    const fill = normalizeHex(safeRead(() => shape.fill.foregroundColor));
    if (fill) template.fillColor = fill;
    const line = normalizeHex(safeRead(() => shape.lineFormat.color));
    if (line) template.lineColor = line;

    // Font colour lives on the text range; some shapes have no text frame, so
    // this read is best-effort and never fails the capture.
    try {
      shape.textFrame.textRange.font.load("color");
      await ctx.sync();
      const font = normalizeHex(shape.textFrame.textRange.font.color);
      if (font) template.fontColor = font;
    } catch (e) {
      logOrgFailure("org-chart-template-font", e);
    }

    return template;
  });
}

/** Read a loaded Office.js property, swallowing "property not available" throws. */
function safeRead(getter: () => string | null | undefined): string | null | undefined {
  try {
    return getter();
  } catch {
    return null;
  }
}

/**
 * Add a slide to the end of the deck, select it (so photo insertion targets it)
 * and return it.
 */
async function addNewSlide(ctx: PowerPoint.RequestContext): Promise<PowerPoint.Slide> {
  const p = ctx.presentation;
  p.slides.add();
  await ctx.sync();
  const count = p.slides.getCount();
  await ctx.sync();
  const slide = p.slides.getItemAt(count.value - 1);
  slide.load("id");
  await ctx.sync();
  try {
    p.setSelectedSlides([slide.id]);
    await ctx.sync();
  } catch (e) {
    // Selecting the slide is best-effort; if it fails, photo insertion may land
    // on the previously-active slide, but the cards are already correct.
    logOrgFailure("org-chart-select-new-slide", e);
  }
  return slide;
}

/** Resolve the target slide: the selected one, else the first slide. */
async function resolveSlide(ctx: PowerPoint.RequestContext): Promise<PowerPoint.Slide> {
  // Load the selection and the full slide list together so the choice below
  // needs only a single round-trip instead of one sync per branch.
  const selected = ctx.presentation.getSelectedSlides();
  const slides = ctx.presentation.slides;
  selected.load("items");
  slides.load("items");
  try {
    await ctx.sync();
  } catch (e) {
    // Surface unexpected Office.js failures to telemetry before propagating —
    // the caller (useOrgChart) turns the rethrow into a user-facing error.
    logOfficeJsError("org-chart-resolve-slide", e);
    throw e;
  }

  const chosen = selected.items[0];
  if (chosen) {
    return chosen;
  }

  const first = slides.items[0];
  if (!first) {
    throw new Error(ERR_NO_SLIDES);
  }
  return first;
}

function addConnectors(slide: PowerPoint.Slide, connectors: readonly Connector[]): void {
  connectors.forEach((seg, i) => {
    const left = Math.min(seg.x1, seg.x2);
    const top = Math.min(seg.y1, seg.y2);
    const width = Math.abs(seg.x2 - seg.x1) || MIN_CONNECTOR_DIM;
    const height = Math.abs(seg.y2 - seg.y1) || MIN_CONNECTOR_DIM;
    const line = slide.shapes.addLine(PowerPoint.ConnectorType.straight, {
      left,
      top,
      width,
      height,
    });
    line.name = CONNECTOR_PREFIX + i;
    line.lineFormat.color = LINE_COLOR;
    line.lineFormat.weight = LINE_WEIGHT;
  });
}

/** One card's shapes plus the text boxes it contributes for later styling. */
interface CardShapes {
  nodeId: number;
  shapes: PowerPoint.Shape[];
  texts: StyledText[];
}

function addCard(
  slide: PowerPoint.Slide,
  node: PositionedNode,
  style: CardStyle,
  showPhotoHead: boolean,
): CardShapes {
  const cardColor = style.fill;
  const shapes: PowerPoint.Shape[] = [];
  const texts: StyledText[] = [];

  // A square avatar band on the left when photo heads are on.
  const avatarSize = showPhotoHead
    ? Math.min(node.height - AVATAR_INSET, node.width * AVATAR_WIDTH_RATIO)
    : 0;
  const avatarMargin = showPhotoHead ? (node.height - avatarSize) / 2 : 0;
  const textLeft = showPhotoHead
    ? node.x + avatarSize + avatarMargin + TEXT_PAD_X
    : node.x + TEXT_PAD_X;
  const textWidth = showPhotoHead
    ? node.width - avatarSize - avatarMargin - 2 * TEXT_PAD_X
    : node.width - 2 * TEXT_PAD_X;

  // Card background first so it renders behind the avatar and text.
  const card = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.roundRectangle);
  card.left = node.x;
  card.top = node.y;
  card.width = node.width;
  card.height = node.height;
  card.fill.setSolidColor(cardColor);
  card.lineFormat.visible = true;
  card.lineFormat.color = style.line;
  card.lineFormat.weight = CARD_BORDER_WEIGHT;
  card.name = CARD_PREFIX + node.id;
  shapes.push(card);

  // Initials avatar.
  if (showPhotoHead) {
    const avatarTop = node.y + (node.height - avatarSize) / 2;
    const circle = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.ellipse);
    circle.left = node.x + avatarMargin;
    circle.top = avatarTop;
    circle.width = avatarSize;
    circle.height = avatarSize;
    circle.fill.setSolidColor(WHITE);
    circle.lineFormat.visible = true;
    circle.lineFormat.color = cardColor;
    circle.lineFormat.weight = CARD_BORDER_WEIGHT;
    circle.name = AVATAR_PREFIX + node.id;
    shapes.push(circle);

    const initialsBox = slide.shapes.addTextBox(getInitials(node.name));
    initialsBox.left = node.x + avatarMargin;
    initialsBox.top = avatarTop;
    initialsBox.width = avatarSize;
    initialsBox.height = avatarSize;
    initialsBox.name = INITIALS_PREFIX + node.id;
    shapes.push(initialsBox);
    // Dark initials on the light (white) avatar fill; flip if that ever changes.
    const initialsColor = luminance(WHITE) > LIGHT_LUMINANCE_THRESHOLD ? cardColor : WHITE;
    texts.push({
      box: initialsBox,
      bold: true,
      size: Math.max(MIN_AVATAR_FONT, Math.round(avatarSize * AVATAR_FONT_RATIO)),
      color: initialsColor,
      align: "center",
      vAlign: true,
    });
  }

  // Name (upper part of the card).
  const nameHeight = node.title ? node.height * NAME_BAND_RATIO : node.height - NAME_ONLY_INSET;
  const nameBox = slide.shapes.addTextBox(node.name);
  nameBox.left = textLeft;
  nameBox.top = node.y + TEXT_PAD_TOP;
  nameBox.width = textWidth;
  nameBox.height = nameHeight;
  nameBox.name = NAME_PREFIX + node.id;
  shapes.push(nameBox);
  texts.push({ box: nameBox, bold: true, size: NAME_FONT_SIZE, color: style.text });

  // Title (lower part), only when present.
  if (node.title) {
    const titleBox = slide.shapes.addTextBox(node.title);
    titleBox.left = textLeft;
    titleBox.top = node.y + TEXT_PAD_TOP + nameHeight;
    titleBox.width = textWidth;
    titleBox.height = Math.max(MIN_TITLE_HEIGHT, node.height - nameHeight - TITLE_GAP);
    titleBox.name = TITLE_PREFIX + node.id;
    shapes.push(titleBox);
    texts.push({ box: titleBox, bold: false, size: TITLE_FONT_SIZE, color: style.text });
  }

  return { nodeId: node.id, shapes, texts };
}

/** Clear text-box fills, then load + apply font styling in synced phases. */
async function styleTexts(ctx: PowerPoint.RequestContext, texts: readonly StyledText[]): Promise<void> {
  let clearError: unknown = null;
  for (const t of texts) {
    try {
      t.box.fill.clear();
    } catch (e) {
      // A text box may have no clearable fill; keep going, but keep the error so
      // the outcome is reported once rather than swallowed per-box.
      clearError = e;
    }
  }
  try {
    await ctx.sync();
  } catch (e) {
    clearError = e;
  }
  if (clearError) {
    logOrgFailure("org-chart-fill-clear", clearError);
  }

  for (const t of texts) {
    t.box.textFrame.load("textRange,verticalAlignment");
  }
  await ctx.sync();

  let styleError: unknown = null;
  for (const t of texts) {
    try {
      t.box.textFrame.textRange.font.size = t.size;
      t.box.textFrame.textRange.font.color = `#${t.color}`;
      t.box.textFrame.textRange.font.bold = t.bold;
      if (t.align === "center") {
        t.box.textFrame.textRange.paragraphFormat.horizontalAlignment = "Center";
      }
      if (t.vAlign) {
        t.box.textFrame.verticalAlignment = "Middle";
      }
    } catch (e) {
      // Best-effort: a failed box keeps default styling. Reported below.
      styleError = e;
    }
  }
  try {
    await ctx.sync();
  } catch (e) {
    styleError = e;
  }
  if (styleError) {
    logOrgFailure("org-chart-font-style", styleError);
  }
}

/** Group each card's shapes so a person's card moves as one object. */
async function groupCards(
  ctx: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  cards: readonly CardShapes[],
): Promise<void> {
  for (const card of cards) {
    for (const s of card.shapes) {
      s.load("id");
    }
  }
  await ctx.sync();

  let groupError: unknown = null;
  for (const card of cards) {
    if (card.shapes.length < 2) {
      continue;
    }
    try {
      const group = slide.shapes.addGroup(card.shapes.map((s) => s.id));
      group.name = `OrgCardGroup_${card.nodeId}`;
    } catch (e) {
      // Grouping can be unsupported for some shape mixes; the card still renders
      // correctly ungrouped, so a failure here is non-fatal.
      groupError = e;
    }
  }
  try {
    await ctx.sync();
  } catch (e) {
    groupError = e;
  }
  if (groupError) {
    logOrgFailure("org-chart-group", groupError);
  }
}

/** Draw the chart and return the id of the slide it landed on. */
async function insertShapes(
  positioned: readonly PositionedNode[],
  connectors: readonly Connector[],
  style: CardStyle,
  showPhotoHead: boolean,
  insertMode: "current" | "newSlide",
): Promise<string> {
  let slideId = "";
  await ppt().run(async (ctx) => {
    const slide = insertMode === "newSlide" ? await addNewSlide(ctx) : await resolveSlide(ctx);
    slide.load("id");

    // Connectors first so they sit behind the cards.
    addConnectors(slide, connectors);
    await ctx.sync(); // slide.id is loaded after this sync

    const cards = positioned.map((node) => addCard(slide, node, style, showPhotoHead));
    await ctx.sync();

    const texts = cards.flatMap((c) => c.texts);
    await styleTexts(ctx, texts);
    await groupCards(ctx, slide, cards);
    slideId = slide.id;
  });
  return slideId;
}

/**
 * Overlay each person's photo on their avatar. Runs AFTER the cards are drawn.
 * Image insertion (`setSelectedDataAsync`) targets the SELECTED slide, so the
 * chart's slide is selected first — this makes photos work on the current slide
 * as well as a new one. Best-effort per person: a failed photo leaves the
 * initials avatar untouched and never aborts the run.
 */
async function embedPhotos(positioned: readonly PositionedNode[], slideId: string): Promise<void> {
  const withPhotos = positioned.filter((n) => typeof n.photoDataUrl === "string" && n.photoDataUrl);
  if (withPhotos.length === 0) return;

  if (slideId) {
    try {
      await ppt().run(async (ctx) => {
        ctx.presentation.setSelectedSlides([slideId]);
        await ctx.sync();
      });
    } catch (e) {
      // If selection fails the photos may land on the wrong slide; log and press
      // on so the (correct) cards still stand.
      logOrgFailure("org-chart-select-photo-slide", e);
    }
  }

  for (const node of withPhotos) {
    try {
      await embedPhoto(node); // NOSONAR photos are embedded into one slide in order
    } catch (e) {
      logOrgFailure("org-chart-embed-photo", e);
    }
  }
}

/** Strip a `data:` URL down to its raw base64 payload. */
function toBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/** Insert one photo and move/resize it onto the card's avatar square. */
async function embedPhoto(node: PositionedNode): Promise<void> {
  const rect = avatarRect(node);
  const base64 = toBase64(node.photoDataUrl as string);

  await new Promise<void>((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      base64,
      {
        coercionType: Office.CoercionType.Image,
        imageWidth: rect.size,
        imageHeight: rect.size,
      },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error.message));
      },
    );
  });

  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) return;
    slide.shapes.load("items/id");
    await ctx.sync();
    const image = slide.shapes.items[slide.shapes.items.length - 1];
    if (!image) return;
    image.left = rect.left;
    image.top = rect.top;
    image.width = rect.size;
    image.height = rect.size;
    image.name = PHOTO_PREFIX + node.id;
    await ctx.sync();
  });
}
